import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Megaphone, Calendar, MessageCircle, Mail, Users, Clock, Sparkles, ChevronRight } from "lucide-react";
import { format } from "date-fns";
import { sl } from "date-fns/locale";

const NewsSummaryDialog = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!token || !user?.id) return;
    // Show only once per login (per token). Use a session-storage key tied to token signature.
    const key = `news_seen_${token.slice(-16)}`;
    if (sessionStorage.getItem(key)) return;
    let cancelled = false;
    (async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const res = await axios.get(`${API}/me/news-summary`, { headers });
        if (cancelled) return;
        const s = res.data;
        // Don't show if first login or no news at all
        if (s.first_login) {
          sessionStorage.setItem(key, "1");
          return;
        }
        const total =
          (s.obvestila?.length || 0) +
          (s.dogodki?.length || 0) +
          (s.forum_objave || 0) +
          (s.sporocila || 0) +
          (s.cakajoce_ure || 0) +
          (s.novi_clani || 0);
        if (total === 0) {
          sessionStorage.setItem(key, "1");
          return;
        }
        setSummary(s);
        setOpen(true);
        sessionStorage.setItem(key, "1");
      } catch (e) {
        // silently ignore
      }
    })();
    return () => { cancelled = true; };
  }, [token, user?.id]);

  if (!summary) return null;

  const sinceLabel = summary.since
    ? format(new Date(summary.since), "d. MMM yyyy 'ob' HH:mm", { locale: sl })
    : "";

  const go = (path) => {
    setOpen(false);
    navigate(path);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-md" data-testid="news-summary-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            Novosti od zadnjega obiska
          </DialogTitle>
          <p className="text-xs text-muted-foreground">Od {sinceLabel}</p>
        </DialogHeader>

        <div className="space-y-2">
          {summary.obvestila?.length > 0 && (
            <button
              onClick={() => go("/obvestila")}
              className="w-full text-left p-3 rounded-lg border bg-orange-50/50 hover:bg-orange-100/60 transition-colors"
              data-testid="news-obvestila"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Megaphone className="h-4 w-4 text-orange-600" />
                  <span className="font-medium text-sm">Nova obvestila</span>
                  <Badge variant="secondary" className="text-xs">{summary.obvestila.length}</Badge>
                </div>
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              </div>
              <p className="text-xs text-muted-foreground mt-1 line-clamp-1">
                {summary.obvestila.slice(0, 2).map(o => o.naslov).join(" • ")}
              </p>
            </button>
          )}

          {summary.dogodki?.length > 0 && (
            <button
              onClick={() => go("/dogodki")}
              className="w-full text-left p-3 rounded-lg border bg-blue-50/50 hover:bg-blue-100/60 transition-colors"
              data-testid="news-dogodki"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-blue-600" />
                  <span className="font-medium text-sm">Novi dogodki</span>
                  <Badge variant="secondary" className="text-xs">{summary.dogodki.length}</Badge>
                </div>
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              </div>
              <p className="text-xs text-muted-foreground mt-1 line-clamp-1">
                {summary.dogodki.slice(0, 2).map(d => d.naslov).join(" • ")}
              </p>
            </button>
          )}

          {summary.sporocila > 0 && (
            <button
              onClick={() => go("/sporocila")}
              className="w-full text-left p-3 rounded-lg border bg-emerald-50/50 hover:bg-emerald-100/60 transition-colors"
              data-testid="news-sporocila"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-emerald-600" />
                  <span className="font-medium text-sm">Neprebrana sporočila</span>
                  <Badge variant="secondary" className="text-xs">{summary.sporocila}</Badge>
                </div>
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              </div>
            </button>
          )}

          {summary.forum_objave > 0 && (
            <button
              onClick={() => go("/forum")}
              className="w-full text-left p-3 rounded-lg border bg-violet-50/50 hover:bg-violet-100/60 transition-colors"
              data-testid="news-forum"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <MessageCircle className="h-4 w-4 text-violet-600" />
                  <span className="font-medium text-sm">Nove forum objave</span>
                  <Badge variant="secondary" className="text-xs">{summary.forum_objave}</Badge>
                </div>
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              </div>
            </button>
          )}

          {summary.cakajoce_ure > 0 && (
            <button
              onClick={() => go("/delovne-ure")}
              className="w-full text-left p-3 rounded-lg border bg-amber-50/50 hover:bg-amber-100/60 transition-colors"
              data-testid="news-cakajoce-ure"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-amber-600" />
                  <span className="font-medium text-sm">Ure čakajo na potrditev</span>
                  <Badge variant="secondary" className="text-xs">{summary.cakajoce_ure}</Badge>
                </div>
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              </div>
            </button>
          )}

          {summary.preglednik_zahtevki > 0 && (
            <button
              onClick={() => go("/profil")}
              className="w-full text-left p-3 rounded-lg border bg-amber-50/50 hover:bg-amber-100/60 transition-colors"
              data-testid="news-preglednik-zahtevki"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-amber-600" />
                  <span className="font-medium text-sm">Zahtevki za pravice preglednika</span>
                  <Badge variant="secondary" className="text-xs">{summary.preglednik_zahtevki}</Badge>
                </div>
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              </div>
            </button>
          )}

          {summary.novi_clani > 0 && (
            <button
              onClick={() => go("/clani")}
              className="w-full text-left p-3 rounded-lg border bg-slate-50/80 hover:bg-slate-100/80 transition-colors"
              data-testid="news-novi-clani"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-slate-600" />
                  <span className="font-medium text-sm">Novi člani</span>
                  <Badge variant="secondary" className="text-xs">{summary.novi_clani}</Badge>
                </div>
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              </div>
            </button>
          )}
        </div>

        <div className="flex justify-end pt-2">
          <Button variant="outline" onClick={() => setOpen(false)} data-testid="news-summary-close">
            V redu
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default NewsSummaryDialog;
